import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import Layout from '../components/Layout';
import { useToast } from '../components/Toast';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const STATUS: Record<string, { label: string; cls: string }> = {
  active: { label: 'Actief', cls: 'bg-emerald-100 text-emerald-700' },
  suspended: { label: 'Geschorst', cls: 'bg-amber-100 text-amber-700' },
  blocked: { label: 'Geblokkeerd', cls: 'bg-red-100 text-red-700' },
  inactive: { label: 'Wacht op goedkeuring', cls: 'bg-slate-100 text-slate-600' },
};

// dropdown-keuze -> endpoint
const ACTIE: Record<string, string> = { active: 'reactivate', suspended: 'suspend', blocked: 'block' };

export const AdminToegankelijkheidPage: React.FC = () => {
  const navigate = useNavigate();
  const { accessToken, isAuthenticated } = useAuthStore();
  const headers = { Authorization: `Bearer ${accessToken}` };
  const notify = useToast();

  const [colls, setColls] = useState<any[]>([]);
  const [cid, setCid] = useState('');
  const [rows, setRows] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');

  useEffect(() => {
    if (!isAuthenticated || !accessToken) { navigate('/login'); return; }
    fetch(`${API_BASE_URL}/api/settlement/collectivities-list`, { headers })
      .then((r) => (r.ok ? r.json() : [])).then((d) => { setColls(d); if (d[0]) setCid(d[0].id); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, accessToken]);

  const load = useCallback(() => {
    if (!cid) return;
    setLoading(true);
    fetch(`${API_BASE_URL}/api/admin/toegankelijkheid?collectivity_id=${cid}`, { headers })
      .then((r) => (r.ok ? r.json() : [])).then(setRows).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cid, accessToken]);
  useEffect(() => { load(); }, [load]);

  const wijzig = async (id: string, nieuw: string, huidig: string) => {
    if (nieuw === huidig) return;
    if (nieuw === 'blocked' && !window.confirm('Lid blokkeren? De SEPA-machtiging wordt ingetrokken; het lid moet die vernieuwen voordat verzekeren/incasso/checks weer werken.')) return;
    setBusy(id);
    try {
      const r = await fetch(`${API_BASE_URL}/api/admin/members/${id}/${ACTIE[nieuw]}`, { method: 'POST', headers });
      if (!r.ok) { const e = await r.json().catch(() => ({})); throw new Error(e.detail || 'Wijzigen mislukt'); }
      const labels: Record<string, string> = {
        active: 'Lid is op actief gezet.',
        suspended: 'Lid is geschorst — geen toegang tot het portaal.',
        blocked: 'Lid is geblokkeerd — SEPA-machtiging ingetrokken, lid is gevraagd te vernieuwen.',
      };
      notify.success(labels[nieuw], 'Toegang gewijzigd');
      load();
    } catch (e: any) { notify.error(e.message || 'Wijzigen mislukt'); }
    finally { setBusy(''); }
  };

  return (
    <Layout>
      <div className="mb-5 flex items-end justify-between gap-4 flex-wrap">
        <p className="text-sm text-slate-500 max-w-2xl">
          Beheer de toegang van leden per collectief. <strong>Actief</strong> = normale toegang.
          <strong> Geschorst</strong> = geen toegang (bv. overmatig gebruik; financiële gegevens blijven intact).
          <strong> Geblokkeerd</strong> = na een mislukte betaling; de SEPA-machtiging wordt ingetrokken en het lid moet deze vernieuwen.
        </p>
        <select value={cid} onChange={(e) => setCid(e.target.value)} className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white">
          {colls.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-500">Laden...</div>
      ) : rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center text-slate-500">Geen leden in dit collectief.</div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto"><table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50"><tr>
              {['Lid', 'E-mail', 'SEPA-machtiging', 'Status', 'Wijzig toegang'].map((h) => (
                <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-slate-500 uppercase">{h}</th>
              ))}
            </tr></thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((m) => {
                const st = STATUS[m.status] || { label: m.status, cls: 'bg-slate-100 text-slate-600' };
                return (
                  <tr key={m.id} className="hover:bg-slate-50">
                    <td className="px-5 py-3">
                      <button onClick={() => navigate(`/backoffice/members/${m.id}`)} className="font-medium text-mfb-blue hover:underline">{m.company_name}</button>
                      <div className="text-xs text-slate-400">{m.full_name}</div>
                    </td>
                    <td className="px-5 py-3 text-slate-600">{m.email}</td>
                    <td className="px-5 py-3">
                      {m.machtiging_actief
                        ? <span className="text-xs font-medium text-emerald-700">Actief</span>
                        : <span className="text-xs font-medium text-amber-700">Geen / te vernieuwen</span>}
                    </td>
                    <td className="px-5 py-3"><span className={`px-3 py-1 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span></td>
                    <td className="px-5 py-3">
                      <select disabled={busy === m.id} value={['active', 'suspended', 'blocked'].includes(m.status) ? m.status : ''}
                        onChange={(e) => wijzig(m.id, e.target.value, m.status)}
                        className="px-2 py-1.5 border border-slate-300 rounded-lg text-sm bg-white">
                        {!['active', 'suspended', 'blocked'].includes(m.status) && <option value="">— kies —</option>}
                        <option value="active">Actief</option>
                        <option value="suspended">Geschorst</option>
                        <option value="blocked">Geblokkeerd</option>
                      </select>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table></div>
        </div>
      )}
    </Layout>
  );
};

export default AdminToegankelijkheidPage;
